import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import DiffView from "@/components/DiffView";
import StatBox from "@/components/StatBox";
import CertificateBadge from "@/components/CertificateBadge";
import PersonalBestBadge from "@/components/PersonalBestBadge";
import { Textarea } from "@/components/ui/textarea";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import AdaptivePicks from "@/components/AdaptivePicks";
import { keyClickHandler } from "@/lib/keyboardSound";
import CountdownTimer from "@/components/CountdownTimer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { showBadgeUnlocks, showMistakesAddedToast } from "@/lib/badgeToast";
import { Play, Pause, RotateCcw, ArrowLeft, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DictationPractice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [typed, setTyped] = useState("");
  const [start, setStart] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const onKey = useMemo(() => keyClickHandler(), []);
  const submittedRef = useRef(false);
  const [timeUp, setTimeUp] = useState(false);

  useEffect(() => {
    api.get(`/dictations/${id}`).then((r) => setItem(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  useEffect(() => {
    if (!start) return;
    const t = setInterval(() => setElapsed((Date.now() - start) / 1000), 200);
    return () => clearInterval(t);
  }, [start]);

  const handleTypeStart = () => {
    if (!start) setStart(Date.now());
  };

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
      if (!start) setStart(Date.now()); // 15-min timer begins on first audio play
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const restartAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play();
    setPlaying(true);
    if (!start) setStart(Date.now());
  };

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    if (!auto && !typed.trim()) {
      toast.error("Type something first");
      return;
    }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const seconds = start ? (Date.now() - start) / 1000 : 60;
      const { data } = await api.post("/attempts", {
        module: "dictation",
        item_id: id,
        typed_text: typed,
        time_seconds: seconds,
      });
      setResult(data);
      toast.success(auto
        ? `Time's up! Auto-submitted — ${data.accuracy_percent}%`
        : `Accuracy: ${data.accuracy_percent}%`);
      showBadgeUnlocks(data.new_badges);
      showMistakesAddedToast(data.mistakes_added);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch (e) {
      toast.error("Submission failed");
      submittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimeUp = () => {
    setTimeUp(true);
    setTimeout(() => submit(true), 300);
  };

  const reset = () => {
    setTyped("");
    setStart(null);
    setElapsed(0);
    setResult(null);
  };

  const audioSrc = item?.audio_url ? `${BACKEND_URL}${item.audio_url}` : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Link to={item?.language === "hi" ? "/hindi-dictation" : "/dictation"} className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-gold" data-testid="back-to-dictations">
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> {item?.language === "hi" ? "आशु लिपिक सूची" : "Back to Dictations"}
        </Link>

        {!item ? (
          <div className="mt-8 font-mono text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="mt-4 mb-8">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold">
                {item.language === "hi" ? "आशु लिपिक (Hindi Shorthand)" : "Dictation"} · {item.wpm} WPM · 5 min audio · 45 min transcription
              </div>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-2" data-testid="dictation-title" lang={item.language || "en"}>{item.title}</h1>
            </div>

            {/* Player */}
            <div className="border-2 border-brand-gold p-6 bg-secondary/60 mb-8">
              {audioSrc ? (
                <>
                  <audio
                    ref={audioRef}
                    src={audioSrc}
                    onEnded={() => setPlaying(false)}
                    onPause={() => setPlaying(false)}
                    onPlay={() => setPlaying(true)}
                    className="hidden"
                    data-testid="dictation-audio"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={toggleAudio}
                      className="rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-white h-12 w-12 p-0"
                      data-testid="dictation-play-btn"
                    >
                      {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </Button>
                    <Button
                      onClick={restartAudio}
                      variant="outline"
                      className="rounded-lg border-border h-12 w-12 p-0"
                      data-testid="dictation-restart-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <div className="ml-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Dictation audio</div>
                      <div className="font-mono text-sm">Speed: {item.wpm} WPM · Source: {item.audio_source}</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="font-mono text-sm text-muted-foreground" data-testid="dictation-no-audio">
                  ⚠️ No audio uploaded yet for this dictation. Admin can generate one via TTS or upload MP3.
                </div>
              )}
            </div>

            {/* Countdown timer — 45 min transcription (real exam standard) */}
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <CountdownTimer
                durationSec={45 * 60}
                running={!!start && !result && !timeUp}
                onTimeUp={handleTimeUp}
                testId="dictation-timer"
              />
              <div className="text-xs font-mono text-muted-foreground">
                Auto-submits at 0:00 · timer starts on first audio play or first keystroke
              </div>
            </div>

            {/* Typing area */}
            <div className="mb-4 flex items-end justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  {item.language === "hi" ? "जो आप सुनें, टाइप करें" : "Type what you hear"}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <KeyboardSoundToggle />
              </div>
            </div>
            <Textarea
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
                handleTypeStart();
              }}
              onKeyDown={onKey}
              disabled={timeUp || !!result}
              placeholder={item.language === "hi" ? "जो आप सुनें, वो यहाँ टाइप करें…" : "Start typing as you listen…"}
              className={`min-h-[280px] rounded-lg border-2 border-brand-gold/30 bg-white text-[#0F172A] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:border-brand-gold practice-typing-area font-bold ${item.language === "hi" ? "is-hindi" : "font-mono"}`}
              data-testid="dictation-textarea"
              lang={item.language || "en"}
            />

            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                onClick={submit}
                disabled={submitting}
                className="rounded-lg h-12 px-8 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
                data-testid="dictation-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" /> {submitting ? "Checking…" : "Submit & Check"}
              </Button>
              <Button
                onClick={reset}
                variant="outline"
                className="rounded-lg h-12 px-6 border-border"
                data-testid="dictation-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reset
              </Button>
            </div>

            {result && (
              <section className="mt-12" data-testid="dictation-result">
                <h2 className="text-2xl font-black tracking-tight mb-6">Results</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <StatBox testId="result-accuracy" label="Accuracy" value={result.accuracy_percent} unit="%" tone={result.accuracy_percent >= 90 ? "good" : result.accuracy_percent >= 70 ? "warn" : "bad"} />
                  <StatBox testId="result-errors" label="Errors" value={result.error_percent} unit="%" tone="bad" />
                  <StatBox testId="result-wpm" label="WPM" value={result.wpm} unit="wpm" tone="accent" />
                  <StatBox testId="result-words" label="Words" value={`${result.correct}/${result.total_words}`} tone="default" />
                </div>

                <h3 className="text-lg font-black tracking-tight mb-3">Word-by-word breakdown</h3>
                <CertificateBadge attemptId={result.id} accuracy={result.accuracy_percent} wpm={result.wpm} />
                <PersonalBestBadge result={result} />
                <div className="mt-6">
                  <DiffView result={result.diff} />
                </div>

                <div className="mt-6 border border-border p-6 bg-secondary/60">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Original text</div>
                  <div className="font-mono text-sm leading-relaxed whitespace-pre-wrap">{result.original}</div>
                </div>

                <div className="mt-10">
                  <AdaptivePicks module="dictation" limit={3} title="Try next" />
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
